import { Alert } from "@svea/design-system-components-react";
import {
	AriaPopover,
	Heading,
	Markdown,
	moneyFormatNoDecimal,
	P,
	Stack,
	Text,
	useText,
	Paper,
} from "@sveabank/cc-common";
import { usePageContext } from "../../../../hooks/context";
import { useClcCompanyApplication } from "../../hooks";
import { getDebitCardAddonCost, getTotalAddonCost, getCardholderPersonDetails } from "../../utils";
import ClcCardholder from "./clc-cardholder";
import { foretagarnaPackageMonthlyPrice } from "./foretagarna-helper";

const ClcCards = () => {
	const {
		clcApplicationDetails: {
			applicant,
			companyShares,
			beneficialOwners: beneficialOwnership,
			representatives,
		},
		selectedPackage,
		cards: {
			debitCards: { availableCardHolders, cards },
		},
	} = usePageContext(useClcCompanyApplication);

	const [t, texts] = useText();

	const shareholders = companyShares.value?.shareholders || [];
	const beneficialOwners = beneficialOwnership.value?.beneficialOwners || [];
	const companyRepresentatives = representatives.value?.representatives || [];
	const isForetagarnaPackage = selectedPackage?.packageId === "FÖRETAGARNA-SVEA-STARTA-BOLAG";
	const packages = texts.newCompany.packages;
	const text = packages[selectedPackage?.packageId];

	const possibleCardholders = getCardholderPersonDetails(
		availableCardHolders,
		shareholders,
		companyRepresentatives,
		beneficialOwners,
	);

	const possibleCardholdersWithoutApplicant = possibleCardholders.filter(
		(person) => person.nationalId !== applicant.nationalId,
	);

	const includedCard = cards.find((c) => c.isAddon === false);

	if (!includedCard && selectedPackage)
		throw new Error("Unexpected: No card included in package (clc-cards)");

	const packageText = texts.newCompany.packages[selectedPackage.packageId];
	const packageTotalCost = moneyFormatNoDecimal(
		Number(selectedPackage.monthlyFee.amount) +
			getTotalAddonCost(selectedPackage) +
			(isForetagarnaPackage ? foretagarnaPackageMonthlyPrice : 0),
	);

	return (
		<Stack gap={2}>
			<Stack direction="row" gap={1}>
				<Heading.H3 size="h4" italic={false} className="text-text-primary">
					{t(packageText.debitCard)}
				</Heading.H3>
				<AriaPopover
					variant="info"
					content={() => <Markdown>{t(packageText.debitCardInfoPopover)}</Markdown>}
				/>
			</Stack>
			<Stack gap={4}>
				<P>
					{t(
						packageText.debitCardDescription,
						moneyFormatNoDecimal(+getDebitCardAddonCost(selectedPackage)),
					)}
				</P>

				<ClcCardholder person={applicant} existingCard={includedCard} />

				{possibleCardholdersWithoutApplicant.map((cardholder) => {
					const existingCard = cards.find((c) => c.nationalId === cardholder.nationalId);
					return (
						<ClcCardholder
							key={cardholder.nationalId}
							person={cardholder}
							existingCard={existingCard}
						/>
					);
				})}
				{cards.some((c) => c.isAddon && c.isConfigured) && (
					<Text align="end" bold>
						{t(
							text.summary.extraDebitCardsTotalCost,
							getTotalAddonCost(selectedPackage).toString(),
						)}
					</Text>
				)}

				<Alert kind="information" title={t(text.noticeHeader)}>
					{t(text.notice)}
				</Alert>

				<Paper plain className="bg-background-complementary-gray" padding="base">
					<Stack>
						<Text>{t(texts.newCompany.totalCost)}</Text>
						<Heading.H3 italic={false}>
							{t(texts.newCompany.pricePerMonth, packageTotalCost)}
						</Heading.H3>
					</Stack>
				</Paper>
			</Stack>
		</Stack>
	);
};

export default ClcCards;
